import { Attribute } from '@app/components/various/AttributesList/Attribute';
import { Link } from '@app/components/various/Link';
import { Status } from '@app/components/various/Status';
import { getIsUserKnown, getUser } from '@app/ducks';
import { getIsSeller } from '@app/logic/User';
import { compiledPaths } from '@app/paths';
import { formatDate } from '@app/utils/dates';
import { getAccountNameOrFullName } from '@app/utils/getAccountNameOrFullName';
import { useOrderStatusLabel } from '@app/utils/hooks';
import { useAvailableOrderDetails } from '@app/utils/hooks/useAvailableOrderDetails';
import { isDefined } from '@app/utils/is';
import { Order } from '@typings';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { OrderInformationSkeleton } from './OrderInformationSkeleton';

interface Props {
  orderDetails: Order.Single;
  isLoading?: boolean;
  isEditable: boolean;
}

export const OrderInformation = ({ orderDetails, isLoading = false, isEditable }: Props) => {
  const { t } = useTranslation(['orders', 'common', 'checkout', 'accounts', 'selections']);
  const user = useSelector(getUser);
  const { accountName, accountId, buyerFirstName, buyerLastName } = useAvailableOrderDetails();
  const orderStatusLabel = useOrderStatusLabel(orderDetails.order.status);

  const getAccountName = () => {
    if (!isDefined(orderDetails.account)) {
      return <span>{t('common:deactivated')}</span>;
    }

    const accountLink = compiledPaths.ACCOUNT_DETAILS({ accountId });
    const formattedName = getAccountNameOrFullName(accountName, buyerFirstName, buyerLastName);

    if (getIsUserKnown(user) && getIsSeller(user)) {
      return (
        <Link data-sentry-mask to={accountLink}>
          {formattedName}
        </Link>
      );
    }

    return formattedName;
  };

  if (isLoading) {
    return <OrderInformationSkeleton />;
  }

  return (
    <div className="mb-5 flex flex-wrap items-center gap-x-10 gap-y-3 lg:gap-x-14">
      <Attribute name={t('accounts:account_one')} value={getAccountName()} orientation="vertical" />
      <Attribute
        name={t('checkout:order_status')}
        value={
          <span className="flex items-center gap-1">
            <Status type={orderDetails.order.status} /> <span className="text-neutral-20">{orderStatusLabel}</span>
          </span>
        }
        orientation="vertical"
      />
      <Attribute
        name={isEditable ? t('common:last_modified') : t('checkout:order_date')}
        value={
          isEditable ?
            orderDetails.order.lastModifiedDate && formatDate(orderDetails.order.lastModifiedDate)
          : isDefined(orderDetails.order.orderDate) && formatDate(orderDetails.order.orderDate)
        }
        orientation="vertical"
      />
    </div>
  );
};
